import { ConnectionQuality, Track, type Participant, type TrackPublication, type VideoTrack } from 'livekit-client';
import { ChevronUp, Maximize2, Mic, MicOff, Minimize2, Monitor, MonitorOff, PhoneOff, Signal, Video, VideoOff, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { firstName } from '../lib/avatar';
import { canShareScreen, useHasKeyboard } from '../lib/media';
import { useVoice } from '../state/voice';
import { Avatar } from './Avatar';
import { ProfileButton } from './ProfileCard';
import { AudioMenu, VideoMenu } from './CallMenus';
import { useDismiss } from '../lib/dismiss';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useChannels } from './Sidebar';

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const MIC_SHORTCUT = isMac ? '⌥ M' : 'Alt + M';

export interface Person {
  identity: string;
  name: string;
  isLocal: boolean;
  micOn: boolean;
  camera: VideoTrack | null;
  screen: TrackPublication | null;
  quality: ConnectionQuality;
}

function visibleVideo(publication: TrackPublication | undefined): VideoTrack | null {
  if (!publication?.track || publication.isMuted) return null;
  return publication.track as VideoTrack;
}

/** A plain view of the LiveKit room, rebuilt whenever the voice store bumps its version. */
export function usePeople(): Person[] {
  const room = useVoice((s) => s.room);
  const version = useVoice((s) => s.version);
  return useMemo(() => {
    if (!room) return [];
    const all: Participant[] = [room.localParticipant, ...room.remoteParticipants.values()];
    return all.map((p) => ({
      identity: p.identity,
      name: p.name || p.identity,
      isLocal: p === room.localParticipant,
      micOn: p.isMicrophoneEnabled,
      camera: visibleVideo(p.getTrackPublication(Track.Source.Camera)),
      screen: p.getTrackPublication(Track.Source.ScreenShare) ?? null,
      quality: p.connectionQuality,
    }));
    // `version` is the change signal for the mutable LiveKit room.
  }, [room, version]);
}

/** The call inside a channel: tiles (or a watched screen share) and the call controls. `compact` (pop-out window) drops the shortcut hint. */
export function CallStage({ compact = false }: { compact?: boolean }) {
  const voice = useVoice();
  const people = usePeople();
  const hasKeyboard = useHasKeyboard();
  const sharers = people.filter((p) => p.screen);
  // Your own share can be watched too, straight from the local track.
  const watched = sharers.find((p) => voice.watching.has(p.identity) && p.screen?.track);

  return (
    <>
      <section
        aria-label="Katılımcılar"
        className="@container min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(ellipse_60%_55%_at_28%_30%,rgb(89_90_186/0.16)_0%,rgb(255_0_138/0.04)_45%,transparent_75%)] p-3 md:p-4"
      >
        {watched ? (
          <Stage person={watched} people={people} speaking={voice.speaking} onStop={() => voice.unwatch(watched.identity)} />
        ) : (
          <TileGrid count={people.length + sharers.length}>
            {people.map((person) => (
              <PersonTile key={person.identity} person={person} speaking={voice.speaking.has(person.identity)} />
            ))}
            {sharers.map((p) =>
              p.isLocal ? (
                <OwnShareTile key="share-own" person={p} onView={() => voice.watch(p.identity)} onStop={() => void voice.toggleScreen()} />
              ) : (
                <ShareTile key={`share-${p.identity}`} person={p} onWatch={() => voice.watch(p.identity)} />
              ),
            )}
          </TileGrid>
        )}
      </section>
      {!voice.micOn && hasKeyboard && !compact && (
        <div className="flex justify-center px-5 pb-3">
          <p className="m-0 flex flex-wrap items-center justify-center gap-2 rounded-full bg-surface-2 px-3.5 py-[7px] text-[13px] text-fg-2">
            <MicOff size={15} aria-hidden="true" />
            Mikrofonun kapalı. Konuşmak için
            <kbd className="rounded-xs border border-line-strong bg-surface-3 px-[7px] py-0.5 font-mono text-xs text-fg-1">{MIC_SHORTCUT}</kbd>
            tuşlarına bas.
          </p>
        </div>
      )}
      <Controls quality={people.find((p) => p.isLocal)?.quality ?? ConnectionQuality.Unknown} />
    </>
  );
}

/** Messages from the voice store: kicked, moved to another device, connection trouble. */
export function Notice() {
  const text = useVoice((s) => s.notice);
  const rejoin = useVoice((s) => s.rejoin);
  if (!text) return null;
  return (
    <div role="status" className="mx-3 mt-3 flex animate-enter items-center gap-3 rounded-md border border-line-strong bg-surface-2 px-3.5 py-2.5 text-sm md:mx-5">
      <span className="flex-1">{text}</span>
      {rejoin && (
        <button type="button" onClick={() => void useVoice.getState().join(rejoin)} className="nc-press h-8 rounded-sm border-0 bg-brand-500 px-3 text-[13px] font-bold text-white">
          Buraya geri al
        </button>
      )}
      <button type="button" aria-label="Kapat" onClick={() => useVoice.getState().dismissNotice()} className="grid size-7 place-items-center rounded-xs border-0 bg-transparent text-fg-2 hover:bg-surface-3">
        <X size={15} />
      </button>
    </div>
  );
}

export function VideoView({ track, mirror = false, fit = 'cover' }: { track: VideoTrack; mirror?: boolean; fit?: 'cover' | 'contain' }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [track]);
  return (
    <video
      ref={ref}
      muted
      playsInline
      autoPlay
      className={`absolute inset-0 size-full ${fit === 'cover' ? 'object-cover' : 'object-contain'} ${mirror ? '-scale-x-100' : ''}`}
    />
  );
}

function NameTag({ person }: { person: Person }) {
  return (
    <ProfileButton
      user={{ id: person.identity, name: person.name }}
      className="absolute bottom-2 left-2 flex max-w-[calc(100%-16px)] items-center gap-1.5 truncate rounded-[9px] bg-black/55 py-[5px] pr-2.5 pl-2 text-[13px] font-semibold backdrop-blur-sm hover:bg-black/70 md:bottom-3 md:left-3"
    >
      {person.micOn ? <Mic size={14} className="text-brand-400" /> : <MicOff size={14} className="text-fg-3" />}
      {firstName(person.name)}
      {person.isLocal && <span className="font-medium text-fg-3">(sen)</span>}
      {(person.quality === ConnectionQuality.Poor || person.quality === ConnectionQuality.Lost) && (
        <Signal size={13} className="text-red-400" aria-label="Bağlantısı zayıf" />
      )}
    </ProfileButton>
  );
}

/**
 * Columns follow the number of tiles and the width of the pane itself (container queries), not
 * the viewport: a tablet's pane next to the sidebar is narrower than a phone in landscape.
 * (`auto-fit` was no good: it counts columns by their maximum width and collapses to one.)
 */
function TileGrid({ count, children }: { count: number; children: React.ReactNode }) {
  const narrow = count <= 1 ? 1 : 2;
  const medium = count <= 1 ? 1 : count <= 4 ? 2 : 3;
  const wide = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
  const maxWidth = ['', '720px', '1100px', '1280px', 'none'][wide];
  return (
    <div
      className="mx-auto grid min-h-full w-full grid-cols-[repeat(var(--cols-n),minmax(0,1fr))] content-center gap-2.5 @xl:grid-cols-[repeat(var(--cols-m),minmax(0,1fr))] @xl:gap-3 @5xl:max-w-(--grid-max) @5xl:grid-cols-[repeat(var(--cols-w),minmax(0,1fr))]"
      style={{ ['--cols-n' as string]: narrow, ['--cols-m' as string]: medium, ['--cols-w' as string]: wide, ['--grid-max' as string]: maxWidth }}
    >
      {children}
    </div>
  );
}

function PersonTile({ person, speaking, compact = false }: { person: Person; speaking: boolean; compact?: boolean }) {
  return (
    <div
      className={`group relative grid place-items-center overflow-hidden rounded-[18px] [container-type:size] ${speaking ? 'nc-tile-live' : 'border border-line bg-surface-2'} ${compact ? 'h-24 w-32 flex-none md:h-32 md:w-48' : 'aspect-[4/3] @xl:aspect-video'}`}
    >
      {person.camera ? (
        <VideoView track={person.camera} mirror={person.isLocal} />
      ) : (
        // Lifted a little so the name tag never covers it in small tiles. The speaking ring may grow
        // as far as the tile's top edge: half the tile's height minus the lifted avatar's half.
        <Avatar id={person.identity} name={person.name} size="fluid" speaking={speaking} large className="-translate-y-[6%] [--nc-ring-max:calc(50cqh_-_0.56_*_var(--av))]" />
      )}
      <NameTag person={person} />
      {!person.isLocal && !compact && <VolumeControl identity={person.identity} name={firstName(person.name)} />}
    </div>
  );
}

/** Per-person volume; visible on hover with a mouse, always on touch screens. */
function VolumeControl({ identity, name }: { identity: string; name: string }) {
  const volume = useVoice((s) => s.volumes[identity] ?? 1);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  return (
    <div ref={box} className="absolute top-2 right-2 flex items-center gap-2 md:top-3 md:right-3">
      {open && (
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(volume * 100)}
          aria-label={`${name} ses seviyesi`}
          onChange={(e) => useVoice.getState().setVolume(identity, Number(e.target.value) / 100)}
          className="w-24 accent-brand-500"
        />
      )}
      <button
        type="button"
        aria-label={`${name} ses seviyesi`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`grid size-8 place-items-center rounded-sm border-0 bg-black/55 text-fg-1 backdrop-blur-sm [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 ${open || volume !== 1 ? '[@media(hover:hover)]:opacity-100' : ''}`}
      >
        {volume === 0 ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
    </div>
  );
}

function ShareTile({ person, onWatch }: { person: Person; onWatch: () => void }) {
  return (
    // Its own container: a small tile (six people) keeps the badge and the button, not the sentence.
    <div className="@container relative col-span-full flex aspect-video flex-col items-center justify-center gap-3.5 overflow-hidden rounded-[18px] border border-line bg-surface-2 p-5 pt-11 text-center @xl:col-span-1 @max-xs:p-3 @max-xs:pt-10">
      <span className="absolute top-3 left-3 flex animate-chip-pulse items-center gap-[5px] rounded-[7px] bg-brand-500 px-2 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
        <Monitor size={12} />
        CANLI
      </span>
      <p className="m-0 text-sm text-fg-2 @max-xs:hidden">
        <span className="font-bold text-fg-1">{firstName(person.name)}</span> ekranını paylaşıyor
      </p>
      <button type="button" onClick={onWatch} className="nc-press nc-cta flex h-[42px] items-center gap-2 rounded-md border-0 bg-brand-500 px-[18px] text-sm font-bold text-white">
        <Monitor size={16} />
        Yayını izle
      </button>
    </div>
  );
}

/** What you share, as the others see it: from the local track, so nothing extra goes over the network. */
function OwnShareTile({ person, onView, onStop }: { person: Person; onView: () => void; onStop: () => void }) {
  const track = person.screen?.track as VideoTrack | undefined;
  const overlay = 'nc-press flex h-9 items-center gap-1.5 rounded-sm border-0 bg-black/60 px-3 text-[13px] font-semibold text-fg-1 backdrop-blur-sm';
  return (
    <div role="group" aria-label="Paylaştığın ekran" className="relative col-span-full aspect-video overflow-hidden rounded-[18px] border border-line bg-black @xl:col-span-1">
      {track ? (
        <VideoView track={track} fit="contain" />
      ) : (
        <span className="grid size-full place-items-center">
          <Monitor size={28} className="text-fg-3" aria-hidden="true" />
        </span>
      )}
      <span className="absolute top-3 left-3 flex animate-chip-pulse items-center gap-[5px] rounded-[7px] bg-brand-500 px-2 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
        <Monitor size={12} />
        CANLI · Senin ekranın
      </span>
      <div className="absolute right-3 bottom-3 flex gap-2">
        {track && (
          <button type="button" onClick={onView} className={overlay}>
            <Maximize2 size={15} aria-hidden="true" />
            Büyüt
          </button>
        )}
        <button type="button" onClick={onStop} className={overlay}>
          <MonitorOff size={15} className="text-red-400" aria-hidden="true" />
          Durdur
        </button>
      </div>
    </div>
  );
}

/** Focus layout while watching a screen share: the share large, everyone else in a strip. */
function Stage({ person, people, speaking, onStop }: { person: Person; people: Person[]; speaking: ReadonlySet<string>; onStop: () => void }) {
  const track = person.screen?.track as VideoTrack | undefined;
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="nc-grad-border relative min-h-0 flex-1 overflow-hidden rounded-[18px]" style={{ ['--nc-fill' as string]: '#000' }}>
        {track && <VideoView track={track} fit="contain" />}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="flex animate-chip-pulse items-center gap-[5px] rounded-[7px] bg-brand-500 px-2 py-1 text-[11px] font-bold tracking-[0.06em] text-white">
            <Monitor size={12} />
            CANLI · {person.isLocal ? 'Senin ekranın' : firstName(person.name)}
          </span>
        </div>
        <button
          type="button"
          onClick={onStop}
          aria-label={person.isLocal ? 'Küçült' : 'İzlemeyi bırak'}
          className="nc-press absolute top-3 right-3 flex h-9 items-center gap-1.5 rounded-sm border-0 bg-black/60 px-3 text-[13px] font-semibold text-fg-1"
        >
          {person.isLocal ? <Minimize2 size={15} /> : <MonitorOff size={15} />}
          <span className="max-md:hidden">{person.isLocal ? 'Küçült' : 'İzlemeyi bırak'}</span>
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 md:gap-3">
        {people.map((p) => (
          <PersonTile key={p.identity} person={p} speaking={speaking.has(p.identity)} compact />
        ))}
      </div>
    </div>
  );
}

/** In the same words and colours as the call bar: LiveKit's "good" is only fair (yellow). */
const qualityText: Record<ConnectionQuality, string> = {
  [ConnectionQuality.Excellent]: 'Bağlantı iyi',
  [ConnectionQuality.Good]: 'Bağlantı orta',
  [ConnectionQuality.Poor]: 'Bağlantı zayıf',
  [ConnectionQuality.Lost]: 'Bağlantı koptu',
  [ConnectionQuality.Unknown]: 'Bağlantı ölçülüyor',
};

/** Active controls are green; a closed mic is red so it is never mistaken for an open one. */
const tone = {
  neutral: 'bg-surface-2 text-fg-1 hover:bg-surface-3',
  on: 'bg-green-400/15 text-green-400 hover:bg-green-400/25',
  off: 'bg-red-400/15 text-red-400 hover:bg-red-400/25',
};

function Controls({ quality }: { quality: ConnectionQuality }) {
  const voice = useVoice();
  const [menu, setMenu] = useState<'audio' | 'video' | null>(null);
  const toggleMenu = (which: 'audio' | 'video') => setMenu((current) => (current === which ? null : which));
  const button = 'nc-press grid h-12 place-items-center border-0 md:h-[52px]';
  const caret = `${button} w-7 border-l border-line-strong ${tone.neutral}`;

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 border-t border-line bg-surface-0 px-3 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] md:px-5">
      <div className={`flex flex-[1_1_140px] items-center gap-2 text-[13px] max-md:hidden ${voice.connection === 'reconnecting' || quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost ? 'text-red-400' : quality === ConnectionQuality.Good ? 'text-yellow-500' : 'text-fg-3'}`}>
        <Signal size={16} aria-hidden="true" />
        {voice.connection === 'reconnecting' ? 'Yeniden bağlanıyor…' : qualityText[quality]}
      </div>
      <div className="flex items-center justify-center gap-2 md:gap-2.5">
        <div className="relative">
          <div className="flex overflow-hidden rounded-lg border border-line-strong">
            <button
              type="button"
              onClick={() => void voice.toggleMic()}
              aria-pressed={voice.micOn}
              aria-label={voice.micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
              aria-disabled={!voice.may.mic && !voice.micOn}
              title={!voice.may.mic && !voice.micOn ? 'Bu kanalda konuşma iznin yok' : `${voice.micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç'} (${MIC_SHORTCUT})`}
              className={`${button} w-12 md:w-[52px] ${voice.micOn ? tone.on : tone.off} aria-disabled:opacity-50`}
            >
              {voice.micOn ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            <button type="button" data-menu-trigger aria-label="Ses seçenekleri" aria-expanded={menu === 'audio'} onClick={() => toggleMenu('audio')} className={caret}>
              <ChevronUp size={16} className={menu === 'audio' ? '' : 'rotate-180'} />
            </button>
          </div>
          {menu === 'audio' && <AudioMenu onClose={() => setMenu(null)} />}
        </div>
        <div className="relative">
          <div className="flex overflow-hidden rounded-lg border border-line-strong">
            <button
              type="button"
              onClick={() => void voice.toggleCamera()}
              aria-label={voice.cameraOn ? 'Kamerayı kapat' : 'Kamerayı aç'}
              aria-pressed={voice.cameraOn}
              aria-disabled={!voice.may.camera && !voice.cameraOn}
              title={!voice.may.camera && !voice.cameraOn ? 'Bu kanalda kamera açma iznin yok' : undefined}
              className={`${button} w-12 md:w-[52px] ${voice.cameraOn ? tone.on : tone.neutral} aria-disabled:opacity-50`}
            >
              {voice.cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
            <button type="button" data-menu-trigger aria-label="Kamera seçenekleri" aria-expanded={menu === 'video'} onClick={() => toggleMenu('video')} className={caret}>
              <ChevronUp size={16} className={menu === 'video' ? '' : 'rotate-180'} />
            </button>
          </div>
          {menu === 'video' && <VideoMenu onClose={() => setMenu(null)} />}
        </div>
        {canShareScreen && (
          <button
            type="button"
            onClick={() => void voice.toggleScreen()}
            aria-label={voice.screenOn ? 'Paylaşımı durdur' : 'Ekranını paylaş'}
            aria-pressed={voice.screenOn}
            aria-disabled={!voice.may.screen && !voice.screenOn}
            title={!voice.may.screen && !voice.screenOn ? 'Bu kanalda ekran paylaşma iznin yok' : undefined}
            className={`${button} w-12 rounded-lg border border-line-strong md:w-[52px] ${voice.screenOn ? tone.on : tone.neutral} aria-disabled:opacity-50`}
          >
            {voice.screenOn ? <MonitorOff size={20} /> : <Monitor size={20} />}
          </button>
        )}
        <LeaveButton />
      </div>
      {/* Balances the connection label so the buttons stay centred; settings live in the sidebar and the mic menu. */}
      <div className="flex-[1_1_140px] max-md:hidden" aria-hidden="true" />
    </div>
  );
}

/**
 * Leaving; for someone whose roles allow it (voice_moderate), a small menu that can also end the
 * call for everyone ("Görüşmeyi sonlandır"; the server checks nobody above them is in it).
 */
function LeaveButton() {
  const channelId = useVoice((s) => s.channelId);
  const { data: channels } = useChannels();
  const canEnd = !!channels?.find((c) => c.id === channelId)?.permissions.includes('voice_moderate');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  const leave = () => void useVoice.getState().leave();
  async function end() {
    setOpen(false);
    if (!channelId) return;
    try {
      await client.api.endCall(channelId);
    } catch (e) {
      useVoice.setState({ notice: errorMessage(e) });
      return;
    }
    // Out at once, without waiting for LiveKit to drop us with everyone else.
    leave();
  }
  const item = 'flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-3 py-2.5 text-left text-[13.5px] hover:bg-surface-3';
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={canEnd ? () => setOpen((o) => !o) : leave}
        aria-label="Görüşmeden ayrıl"
        aria-haspopup={canEnd ? 'menu' : undefined}
        aria-expanded={canEnd ? open : undefined}
        className="nc-press grid h-12 w-16 place-items-center rounded-lg border-0 bg-red-400 text-white md:h-[52px] md:w-[68px]"
      >
        <PhoneOff size={20} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 bottom-full z-20 mb-2 w-60 animate-enter rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
          <button type="button" role="menuitem" onClick={leave} className={`${item} text-fg-1`}>
            Görüşmeden ayrıl
          </button>
          <button type="button" role="menuitem" onClick={() => void end()} className={`${item} font-semibold text-[#EE9191]`}>
            Görüşmeyi herkes için sonlandır
          </button>
        </div>
      )}
    </div>
  );
}
